import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { ApiService } from '@shared/services/api.service';
import { AuthService } from '@shared/services/auth.service';
import { LocaleService } from '@shared/services/locale.service';
import { SnackService } from '@shared/services/snack.service';
import { TransferCompleteDialogComponent } from '../transfer-complete-dialog/transfer-complete-dialog.component';
import { environment as env } from '@environments/environment';
import { DestroyService } from '@core/services/destroy.service';
import { ViewportService } from '@shared/services/viewport.service';
import { ButtonType } from '@shared/components/button/button.component';
import { Clipboard } from '@angular/cdk/clipboard';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Title } from '@angular/platform-browser';
import { NumbersService } from '@shared/services/numbers.service';
import { User } from '@core/models';

@Component({
	selector: 'app-payment-code',
	templateUrl: './payment-code.component.html',
	styleUrls: ['./payment-code.component.scss'],
	providers: [DestroyService],
})
export class PaymentCodeComponent implements OnInit {
	constructor(
		private router: Router,
		private snack: SnackService,
		private api: ApiService,
		private route: ActivatedRoute,
		public locale: LocaleService,
		private dialog: MatDialog,
		private auth: AuthService,
		public viewport: ViewportService,
		private clipboard: Clipboard,
		private readonly deviceDetectorService: DeviceDetectorService,
		private title: Title,
		public numbers: NumbersService
	) {}

	embedded: boolean = false;
	adID: string;
	user: User = this.auth.user();
	loading: boolean = false;
	paymentMode: string;
	package: string;
	vouchers: string[] = [];
	voucherCompany = [];
	adTitle: any;
	step: number = 1;
	provider: string;
	asiaCellData: any;
	korekData: any;
	readonly buttonType = ButtonType;
	isPaymentEnabled = false;
	voucherForm: FormGroup = new FormGroup({
		voucherCodeValue: new FormControl('', [Validators.minLength(13), Validators.maxLength(16), Validators.required, Validators.pattern('[0-9]{0,}')]),
	});

	onOpenWhatsapp() {
		const targetDisplay = this.deviceDetectorService.isMobile() ? '_self' : '_blank';

		window.open(env.WHATSAPP.callForHelpUrl, targetDisplay);
	}

	onCopyToClipboard() {
		this.clipboard.copy(this.asiaCellData.upgradeToVip);

		this.snack.open(this.locale.translations['payment']['n619']);
		this.isPaymentEnabled = true;
	}

	onCall() {
		const COPIED_DATA = this.provider === 'asiacell' ? this.asiaCellData.upgradeToVip : this.korekData.upgradeToVip;
		window.location.href = 'tel:' + COPIED_DATA;
		this.isPaymentEnabled = true;
	}

	//voucherForm return error message
	getErrorMessage(controlName, label) {
		return this.voucherForm.get(controlName).hasError('required')
			? `${label} ${this.locale.translations['payment']['n421']}` //required
			: this.voucherForm.get(controlName).hasError('pattern')
			? this.locale.translations['payment']['n422'] //Only numbers allowed
			: this.voucherForm.get(controlName).hasError('minlength')
			? `${this.locale.translations['payment']['n423']}` //Voucher number must between 13 and 16
			: this.voucherForm.get(controlName).hasError('maxlength')
			? `${this.locale.translations['payment']['n423']}` //Voucher number must between 13 and 16
			: '';
	}

	onKeyUp(field: any, form: FormGroup) {
		form.get(field).setValue(this.numbers.toNumeric(form.get(field).value.toString().replace(/\s/g, '')));
		form.get(field).markAsTouched();
	}

	onAddVoucherControl() {
		if (this.voucherForm.get('voucherCodeValue') && !this.voucherForm.get('voucherCodeValue').invalid) {
			const newControlName: string = 'voucherCode' + (this.vouchers.length + 1).toString();

			this.voucherForm.addControl(
				newControlName,
				new FormControl({ value: this.voucherForm.get('voucherCodeValue').value, disabled: true }, [
					Validators.minLength(13),
					Validators.maxLength(16),
					Validators.required,
				])
			);

			this.vouchers.push(newControlName);
			if (this.vouchers.length === 3) {
				this.voucherForm.controls.voucherCodeValue.disable();
			}
			this.voucherCompany.push(+(this.provider === 'asiacell' ? 0 : 1)); //get voucher provider array
			this.voucherForm.get('voucherCodeValue').setValue('');
			this.voucherForm.get('voucherCodeValue').markAsUntouched();
		} else {
			this.voucherForm.get('voucherCodeValue').markAsTouched();
		}
	}

	async setvoucherTitle() {
		this.loading = true;
		const formData: FormData = new FormData();
		formData.append('lang', this.locale.lang);
		formData.append('userID', this.user['userID']);
		formData.append('adID', this.adID);

		this.api.ad(formData).subscribe((res) => {
			this.loading = false;

			if (res.status === '1') {
				this.adTitle = res.response.data.title;
			} else if (res.status === '0') {
				this.adTitle = '';
			}
		});
	}

	completePayment(body) {
		this.api.payVoucher(body).subscribe({
			next: (res) => {
				if (res['status'] === '1') {
					this.router.navigate(['/payment/success'], {
						relativeTo: this.route,
						queryParams: {
							adID: this.adID,
							package: this.package,
						},
					});
				} else {
					this.snack.open(res.message);
				}

				this.loading = false;
			},
			error: (err) => {
				console.error(err);
				this.snack.open(this.locale.translations['misc']['n174']);
				this.loading = false;
			},
		});
	}

	onVoucher() {
		this.loading = true;
		const body: FormData = new FormData();
		var voucherCodes: string[] = [];
		if (this.vouchers)
			this.vouchers.forEach((voucher) => {
				// voucherCodes.push(this.voucherForm['value'][voucher]);
				voucherCodes.push(this.voucherForm.get(voucher).value);
			});

		if (this.vouchers.length < 3 && this.voucherForm.get('voucherCodeValue') && !this.voucherForm.get('voucherCodeValue').invalid) {
			voucherCodes.push(this.voucherForm.get('voucherCodeValue').value);
		}

		body.append('lang', this.locale.lang);
		body.append('adID', this.adID);
		body.append('userID', this.user['userID']);
		body.append('voucherCode[]', voucherCodes.toString());
		body.append('provider[]', this.voucherCompany.toString()); //passing voucher provider
		body.append('platform', 'web');
		body.append('isBasic', this.package === 'basic' ? '1' : this.package === 'vip' ? '0' : '');

		if (this.package) {
			body.append('package', this.package === 'basic' ? '3' : '2');
		}

		this.completePayment(body);
	}

	//delete voucher
	onDeleteVoucher(voucherControl: string, i: number) {
		this.vouchers.splice(i, 1);
		this.voucherCompany.splice(i, 1);
		this.voucherForm.removeControl(voucherControl);
		this.voucherForm.controls.voucherCodeValue.enable();
	}

	onNext() {
		if (this.step === 1) this.step++;
	}

	onBack() {
		if (this.paymentMode === 'mobileBalance' && this.step === 2) {
			this.step--;
		} else {
			window.history.back();
		}
	}

	onMobileBalance() {
		let voucherCode;
		const TRANSFER_DIALOG = this.dialog.open(TransferCompleteDialogComponent, {
			width: '100%',
			height: 'auto',
			maxWidth: '360px',
		});

		TRANSFER_DIALOG.afterClosed().subscribe((res) => {
			if (res) {
				this.loading = true;
				const body: FormData = new FormData();

				if (this.provider === 'asiacell') {
					voucherCode = this.package === 'basic' ? this.asiaCellData.upgradeToBasic : this.asiaCellData.upgradeToVip;
				} else if (this.provider === 'korek') {
					voucherCode = this.package === 'basic' ? this.korekData.upgradeToBasic : this.korekData.upgradeToVip;
				}

				body.append('lang', this.locale.lang);
				body.append('adID', this.adID);
				body.append('title', this.adTitle);
				body.append('userID', this.user['userID']);
				body.append('voucherCode[]', voucherCode);
				body.append('provider[]', this.provider === 'asiacell' ? '0' : '1'); //passing voucher provider
				body.append('platform', 'web');
				body.append('isBasic', this.package === 'basic' ? '1' : this.package === 'vip' ? '0' : '');
				if (this.package) {
					body.append('package', this.package === 'basic' ? '3' : '2');
				}

				this.completePayment(body);
			}
		});
	}

	getProviderDetails() {
		this.loading = true;
		const formData: FormData = new FormData();
		formData.append('lang', this.locale.lang);

		this.api.getProviders(formData).subscribe((res) => {
			if (res.status === '1') {
				this.asiaCellData = res.response.asiacell;
				this.korekData = res.response.korek;

				this.loading = false;
			} else {
				this.loading = false;
				this.snack.open(this.locale.translations['misc']['n174']);
			}
		});
	}

	ngOnInit(): void {
		this.route.queryParams.subscribe((queries: Params) => {
			if (queries['adID']) {
				this.adID = queries['adID'];
				this.setvoucherTitle();
			}

			if (queries['mode']) {
				this.paymentMode = queries['mode'];
			}

			if (queries['package']) {
				this.package = queries['package'];
			}

			if (queries['embedded']) {
				this.embedded = queries['embedded'];
			}

			if (queries['provider']) {
				this.provider = queries['provider'];
			}
		});

		this.title.setTitle(this.locale.translations['payment']['n209'] + ' | ' + this.locale.translations['header']['n0']);
		if (this.paymentMode === 'mobileBalance') {
			this.getProviderDetails();
		}
	}
}
